<script setup lang="ts">
import { computed, ref } from 'vue'

import { useIngredientQuantity } from '../../../composables/use-ingredient-quantity'
import RecipeInterline from './RecipeInterline.vue'
import ServingCalculator from './ServingCalculator.vue'
import AdaSlotMarker from '../../ad-slots/AdaSlotMarker.vue'

import type { RecipeParagraph } from '../../../utils/paragraph-schema/paragraphs'

type IngredientGroup = NonNullable<RecipeParagraph['recipe']['ingredientGroups']>[number]
type Ingredient = NonNullable<IngredientGroup['ingredients']>[number]

const props = withDefaults(defineProps<{
  ingredientGroups?: IngredientGroup[],
  serving?: RecipeParagraph['recipe']['serving']
}>(), {
  ingredientGroups: () => []
})

const { formatQuantity, parseQuantity } = useIngredientQuantity()

const servingRatio = ref(1)

const isGrammIngredient = (ingredient: Ingredient): boolean => {
  const trimmedUnit = ingredient.unit?.trim()
  return !!trimmedUnit && (trimmedUnit === 'g' || trimmedUnit === 'gramm' || trimmedUnit === 'gram')
}

const proportionalIngredientGroups = computed(() => {
  return props.ingredientGroups
    .map(group => {
      return {
        ...group,
        ingredients: group.ingredients?.map(ingredient => {
          const parsedQuantity = parseQuantity(ingredient.quantity)

          let formattedQuantity: string
          if (Number.isNaN(parsedQuantity)) {
            formattedQuantity = ingredient.quantity ?? '' // if not a number, we just display whatever we received
          } else {
            const calculatedQuantity = parsedQuantity * servingRatio.value // if the quantity is a number, we scale it with the serving ratio
            formattedQuantity = isGrammIngredient(ingredient)
              ? String(Math.round(calculatedQuantity)) // if the unit is gram, we don't use fractions and stuff, we round the quantity
              : formatQuantity(calculatedQuantity)
          }
          return { ...ingredient, quantity: formattedQuantity }
        })
      }
    })
})

const onServingChanged = (ratio: number): void => {
  servingRatio.value = ratio
}
</script>

<template>
  <section class="bx-content bx-recipe__ingredients">
    <RecipeInterline title="Zutaten"
                     variant="ingredients" />
    <ServingCalculator :serving="serving"
                       @serving-changed="onServingChanged($event)" />
    <div v-for="(ingredientGroup, index) in proportionalIngredientGroups"
         :key="'ingredientGroups-' + index"
         class="bx-recipe__ingredients">
      <div v-if="ingredientGroup.name"
           class="bx-ingredient-group__title bx-typo--center bx-typo--h4">
        {{ ingredientGroup.name }}
      </div>
      <p v-for="(ingredient, ingredientIndex) in ingredientGroup.ingredients"
         :key="'ingredients-' + ingredientIndex">
        <span>{{ ingredient.quantityPrefix }} {{ ingredient.quantity }}  {{ ingredient.unit }} {{ ingredient.unitPostfix }}</span>
        <span v-if="ingredient.ingredientName">{{ ingredient.ingredientPrefix }} {{ ingredient.ingredientName }} {{ ingredient.ingredientPostfix }}</span>
        <span v-else-if="ingredient.rawText">{{ ingredient.rawText }}</span>
      </p>
      <AdaSlotMarker v-if="index < proportionalIngredientGroups.length - 1"
                     v-bind="{ aboveParagraph: { index: 2, type: 'recipeIngredientGroup' } }" />
    </div>
    <AdaSlotMarker v-bind="{ aboveParagraph: { index: 2, type: 'recipeIngredientGroup' } }" />
  </section>
</template>
